import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MethodMetricsCard } from '@/components/MethodMetricsCard';
import {
  Inbox, Mail, MailOpen, Archive, Trash2, ArrowLeft,
  Clock, User, AtSign, MessageSquare, ChevronRight, StickyNote, Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  unread: { label: 'Unread', color: 'bg-blue-100 text-blue-800', icon: Mail },
  read: { label: 'Read', color: 'bg-gray-100 text-gray-800', icon: MailOpen },
  archived: { label: 'Archived', color: 'bg-amber-100 text-amber-800', icon: Archive },
};

const SubmissionDetail = ({ submission, onBack, token, onRefresh }) => {
  const [notes, setNotes] = useState(submission.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const markAs = async (status) => {
    try {
      await fetch(`${API}/api/contact/submissions/${submission.contact_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      toast.success(`Marked as ${status}`);
      onRefresh();
    } catch { toast.error('Update failed'); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/contact/submissions/${submission.contact_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_notes: notes }),
      });
      toast.success('Notes saved');
      onRefresh();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirm('Delete this submission permanently?')) return;
    try {
      await fetch(`${API}/api/contact/submissions/${submission.contact_id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      onBack();
    } catch { toast.error('Delete failed'); }
  };

  const sc = STATUS_CONFIG[submission.status] || STATUS_CONFIG.unread;

  return (
    <div className="space-y-5" data-testid="inbox-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="inbox-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-foreground">Message from {submission.name}</h2>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />{new Date(submission.created_at).toLocaleString()}
            <Badge className={`ml-1 text-xs ${sc.color}`}>{sc.label}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {submission.status !== 'read' && <Button variant="outline" size="sm" onClick={() => markAs('read')} data-testid="inbox-mark-read"><MailOpen className="h-4 w-4 mr-1.5" />Mark Read</Button>}
          {submission.status !== 'unread' && <Button variant="outline" size="sm" onClick={() => markAs('unread')} data-testid="inbox-mark-unread"><Mail className="h-4 w-4 mr-1.5" />Mark Unread</Button>}
          {submission.status !== 'archived' && <Button variant="outline" size="sm" onClick={() => markAs('archived')} data-testid="inbox-archive"><Archive className="h-4 w-4 mr-1.5" />Archive</Button>}
          <Button variant="outline" size="sm" className="text-destructive" onClick={handleDelete} data-testid="inbox-delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="rounded-xl border border-muted/50 overflow-hidden">
        <div className="grid grid-cols-2 gap-4 p-5 bg-muted/10 border-b border-muted/50">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="text-sm font-medium text-foreground">{submission.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AtSign className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <a href={`mailto:${submission.email}`} className="text-sm font-medium text-primary hover:underline">{submission.email}</a>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Message</p>
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{submission.message}</p>
        </div>
      </div>

      <div className="rounded-xl border border-muted/50 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin Notes</p>
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          className="w-full text-sm p-3 rounded-lg border border-muted bg-background focus:border-primary/50 focus:outline-none resize-none"
          placeholder="Add internal notes about this submission..."
          data-testid="inbox-notes"
        />
        <Button size="sm" onClick={saveNotes} disabled={saving} data-testid="inbox-save-notes">
          <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving...' : 'Save Notes'}
        </Button>
      </div>
    </div>
  );
};

export const AdminInboxPage = () => {
  const { isMasterAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

  const load = useCallback(async () => {
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const [subsR, statsR] = await Promise.all([
        fetch(`${API}/api/contact/submissions${statusParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/contact/submissions/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (subsR.ok) setSubmissions(await subsR.json());
      if (statsR.ok) setStats(await statsR.json());
    } catch { toast.error('Failed to load inbox'); }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  if (!isMasterAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 text-muted-foreground">
          <Inbox className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">Access restricted to Master Admin</p>
        </div>
      </DashboardLayout>
    );
  }

  const selectedSub = submissions.find(s => s.contact_id === selected);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-inbox-page">
        {selectedSub ? (
          <SubmissionDetail
            submission={selectedSub}
            onBack={() => { setSelected(null); load(); }}
            token={token}
            onRefresh={load}
          />
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Contact Inbox</h2>
                <p className="text-sm text-muted-foreground mt-1">Messages from the landing page contact form</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {[
                { key: 'all', label: 'All', count: stats.total, icon: Inbox },
                { key: 'unread', label: 'Unread', count: stats.unread, icon: Mail },
                { key: 'read', label: 'Read', count: stats.read, icon: MailOpen },
                { key: 'archived', label: 'Archived', count: stats.archived, icon: Archive },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                    filter === f.key ? 'border-primary/50 bg-primary/5 shadow-sm' : 'border-muted/50 hover:border-muted'
                  }`}
                  data-testid={`inbox-filter-${f.key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <f.icon className={`h-4 w-4 ${filter === f.key ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="text-lg font-bold text-foreground">{f.count}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{f.label}</p>
                </button>
              ))}
            </div>

            <MethodMetricsCard />

            {loading ? (
              <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Inbox className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No messages</p>
                <p className="text-sm mt-1">{filter !== 'all' ? `No ${filter} messages` : 'Your inbox is empty'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {submissions.map(sub => {
                  const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.unread;
                  const isUnread = sub.status === 'unread';
                  return (
                    <div
                      key={sub.contact_id}
                      onClick={() => setSelected(sub.contact_id)}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer group ${
                        isUnread ? 'border-primary/20 bg-primary/5 hover:border-primary/40' : 'border-muted/50 hover:border-muted'
                      }`}
                      data-testid={`inbox-item-${sub.contact_id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isUnread ? 'bg-primary/10' : 'bg-muted/30'}`}>
                        {isUnread ? <Mail className="h-4 w-4 text-primary" /> : <MailOpen className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>{sub.name}</p>
                          <Badge className={`text-[10px] ${sc.color}`}>{sc.label}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{sub.email}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{sub.message?.slice(0, 100)}{sub.message?.length > 100 ? '...' : ''}</p>
                      </div>
                      <span className="text-xs text-muted-foreground hidden sm:block whitespace-nowrap">{new Date(sub.created_at).toLocaleDateString()}</span>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminInboxPage;
